import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, currencyFmt, ROLE_LABEL, fmtDateTime, fmtDateOnly } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, CheckCircle2, XCircle, FileText, Loader2, Download, Clock, Pencil, Forward, Printer, Files, FileArchive, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

// Escape untrusted values before interpolating into print/attachment HTML (prevents stored XSS).
const esc = (v) => String(v == null ? "" : v)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export default function ClaimDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [managers, setManagers] = useState([]);
  const [fwdOpen, setFwdOpen] = useState(false);
  const [fwdTo, setFwdTo] = useState("");
  const [fwdNote, setFwdNote] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/claims/${id}`); setClaim(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); navigate("/claims"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (["manager", "admin"].includes(user.role))
      api.get("/tenant/managers").then(({ data }) => setManagers(data)).catch(() => {});
  }, [user.role]);

  const decide = async (action) => {
    if (action === "reject" && !comment.trim()) return toast.error("Rejection reason is required");
    setBusy(true);
    try {
      await api.post(`/claims/${id}/decision`, { action, comment });
      await load();
      toast.success(`Claim ${action === "approve" ? "approved" : "rejected"}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const forward = async () => {
    if (!fwdTo) return toast.error("Select a manager to forward to");
    setBusy(true);
    try {
      await api.post(`/claims/${id}/forward`, { approver_id: fwdTo, note: fwdNote });
      await load();
      setFwdOpen(false); setFwdTo(""); setFwdNote("");
      toast.success("Claim forwarded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const viewAttachment = async (a) => {
    try {
      const { data } = await api.get(`/files/${a.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch { toast.error("Could not open attachment"); }
  };

  const printAttachment = async (a) => {
    try {
      const { data } = await api.get(`/files/${a.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const w = window.open("", "_blank");
      if (!w) return toast.error("Please allow pop-ups to print");
      if ((a.content_type || "").startsWith("image/")) {
        w.document.write(`<html><head><title>${esc(a.filename)}</title><style>@page{size:A4;margin:12mm}html,body{margin:0;height:100%}img{width:100%;height:auto;max-height:100%;object-fit:contain;display:block;margin:0 auto}</style></head><body><img src="${url}" onload="setTimeout(function(){window.focus();window.print();},250)"/></body></html>`);
        w.document.close();
      } else {
        w.location.href = url;
        setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* manual */ } }, 900);
      }
    } catch { toast.error("Could not print attachment"); }
  };

  const allAttachments = [
    ...(claim?.line_items || []).flatMap((li) => (li.attachments || []).map((a) => ({ ...a, _item: li.description }))),
    ...((claim?.attachments) || []).map((a) => ({ ...a, _item: "" })),
  ];

  const printAllAttachments = async () => {
    if (!allAttachments.length) return toast.error("No attachments to print");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print");
    w.document.write(`<html><head><title>${esc(claim.claim_number)} — Attachments</title><style>
      @page{size:A4;margin:12mm}body{margin:0;font-family:Arial,Helvetica,sans-serif}
      section{page-break-after:always;height:calc(100vh - 24mm);display:flex;flex-direction:column}
      section:last-child{page-break-after:auto}
      .cap{font-size:12px;color:#555;margin-bottom:8px;border-bottom:1px solid #eee;padding-bottom:6px}
      .cap b{color:#111}
      img{max-width:100%;max-height:100%;object-fit:contain;margin:auto}
      iframe{border:0;width:100%;height:100%}
      </style></head><body><p style="text-align:center;color:#999">Loading attachments…</p></body></html>`);
    w.document.close();
    try {
      const parts = await Promise.all(allAttachments.map(async (a) => {
        const { data } = await api.get(`/files/${a.path}`, { responseType: "blob" });
        return { a, url: URL.createObjectURL(data) };
      }));
      const sections = parts.map(({ a, url }) => {
        const isImg = (a.content_type || "").startsWith("image/");
        const media = isImg ? `<img src="${url}"/>` : `<iframe src="${url}"></iframe>`;
        const label = a._item ? `${esc(a.filename)} — <span style="color:#777">${esc(a._item)}</span>` : esc(a.filename);
        return `<section><div class="cap">${esc(claim.claim_number)} · <b>${label}</b></div>${media}</section>`;
      }).join("");
      const anyPdf = parts.some((p) => !(p.a.content_type || "").startsWith("image/"));
      w.document.body.innerHTML = sections;
      setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* manual */ } }, anyPdf ? 1400 : 600);
    } catch { toast.error("Could not load attachments"); try { w.close(); } catch (e) { /* noop */ } }
  };

  const printClaim = async () => {
    if (!claim) return;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Please allow pop-ups to print");
    let logoTag = "";
    if (claim.tenant_logo) {
      try {
        const { data } = await api.get(`/tenants/${claim.tenant_id}/logo`, { responseType: "blob" });
        const dataUrl = await new Promise((res) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.readAsDataURL(data);
        });
        logoTag = `<img class="logo" src="${dataUrl}" alt="logo"/>`;
      } catch { /* skip logo */ }
    }
    const money = (v) => currencyFmt(v, claim.currency);
    const rows = (claim.line_items || []).map((li) => `<tr>
      <td>${esc(li.description || "")}</td><td>${esc(li.category || "")}</td><td>${esc(fmtDateOnly(li.expense_date) || "")}</td>
      <td class="r">${money(li.gst_applicable !== false ? li.amount * 3 / 23 : 0)}</td>
      <td class="r">${money(li.amount)}</td></tr>`).join("");
    const decision = claim.status !== "pending"
      ? `<div class="box"><h3>Review Decision</h3><p>${claim.status === "approved" ? "Approved" : "Rejected"} by <b>${esc(claim.reviewed_by_name || "—")}</b></p>${claim.review_comment ? `<p style="color:#555">"${esc(claim.review_comment)}"</p>` : ""}</div>` : "";
    const desc = claim.description ? `<div class="box"><h3>Description</h3><p>${esc(claim.description)}</p></div>` : "";
    w.document.write(`<html><head><title>${esc(claim.claim_number)} — ${esc(claim.title)}</title><style>
      @page{size:A4;margin:14mm}
      body{margin:0;font-family:Arial,Helvetica,sans-serif;color:#111;font-size:12px}
      .voucher{border-bottom:2px solid #111;padding-bottom:10px;margin-bottom:16px;display:flex;align-items:flex-start;justify-content:space-between;gap:16px}
      .voucher .tenant{font-size:18px;font-weight:700}
      .voucher .branch{color:#555;font-size:12px;margin-top:2px}
      .voucher .doc{color:#888;text-transform:uppercase;letter-spacing:1px;font-size:10px;margin-top:6px}
      .voucher .logo{max-height:60px;max-width:180px;object-fit:contain}
      h1{font-size:18px;margin:0 0 4px}
      .sub{color:#666;font-family:monospace;margin:0 0 16px}
      .status{display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;text-transform:uppercase;border:1px solid #ccc;margin-left:8px}
      .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:18px}
      .grid .k{color:#888;text-transform:uppercase;font-size:10px}
      .grid .v{font-weight:600;margin-top:2px}
      table{width:100%;border-collapse:collapse;margin-top:6px}
      th,td{border-bottom:1px solid #eee;padding:8px 6px;text-align:left}
      th{color:#888;text-transform:uppercase;font-size:10px}
      td.r,th.r{text-align:right;font-family:monospace}
      tfoot td{font-weight:700;border-top:2px solid #333}
      .box{margin-top:18px;padding:12px;border:1px solid #eee;border-radius:6px}
      .box h3{font-size:11px;text-transform:uppercase;color:#888;margin:0 0 6px}
      </style></head><body>
      <div class="voucher">
        <div>
          <div class="tenant">${esc(claim.tenant_name || "Claim Q")}</div>
          ${claim.branch_name ? `<div class="branch"><b>${esc(claim.branch_name)}</b>${claim.branch_address ? ` · ${esc(claim.branch_address)}` : ""}</div>` : ""}
          <div class="doc">Expense Reimbursement Voucher</div>
        </div>
        ${logoTag}
      </div>
      <h1>${esc(claim.title)} <span class="status">${esc(claim.status)}</span></h1>
      <p class="sub">${esc(claim.claim_number)} · ${esc(fmtDateOnly(claim.expense_date) || "")}</p>
      <div class="grid">
        <div><div class="k">Expense Type</div><div class="v">${esc(claim.expense_type_label || "—")}</div></div>
        <div><div class="k">Employee</div><div class="v">${esc(claim.employee_name || "—")}</div></div>
        <div><div class="k">Category</div><div class="v">${esc(claim.category || "—")}</div></div>
        <div><div class="k">Currency</div><div class="v">${esc(claim.currency || "—")}</div></div>
        <div><div class="k">Project</div><div class="v">${esc(claim.project_code || "—")}</div></div>
        <div><div class="k">Approver</div><div class="v">${esc(claim.approver_name || "Unassigned")}</div></div>
        <div><div class="k">Total</div><div class="v">${money(claim.total_amount)}</div></div>
      </div>
      ${desc}
      <h3 style="font-size:11px;text-transform:uppercase;color:#888;margin:18px 0 0">Line Items</h3>
      <table><thead><tr><th>Description</th><th>Category</th><th>Date</th><th class="r">GST</th><th class="r">Amount</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="3" class="r" style="color:#888">Totals</td><td class="r">${money(claim.total_gst || 0)}</td><td class="r">${money(claim.total_amount)}</td></tr></tfoot></table>
      ${decision}
      </body></html>`);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* manual */ } }, 400);
  };

  const downloadReceiptsZip = async () => {
    if (!allAttachments.length) return toast.error("No receipts to download");
    try {
      const { data } = await api.get(`/claims/${id}/receipts.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${claim.claim_number}_receipts.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Receipts downloaded");
    } catch { toast.error("Could not download receipts"); }
  };

  if (!claim) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const isPending = claim.status === "pending";
  const isOwnClaim = claim.employee_id === user.user_id;
  const isManagerOrAdmin = ["manager", "admin"].includes(user.role);
  const assignedToMe = !claim.approver_id || claim.approver_id === user.user_id;
  const canDecide = isPending && !isOwnClaim && (user.role === "admin" || (user.role === "manager" && assignedToMe));
  const canForward = isPending && isManagerOrAdmin && !(isOwnClaim && user.role === "manager");
  const canEmployeeEdit = user.role === "employee" && claim.employee_id === user.user_id && ["pending", "rejected"].includes(claim.status);
  const canManageEdit = ["admin", "super_admin"].includes(user.role);
  const canEditClaim = canEmployeeEdit || canManageEdit;
  const forwardTargets = managers.filter((m) => m.user_id !== user.user_id && m.user_id !== claim.approver_id);

  return (
    <div className="max-w-4xl space-y-6" data-testid="claim-detail-page">
      <div>
        <button onClick={() => navigate("/claims")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />Back to claims
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{claim.title}</h1>
              <StatusBadge status={claim.status} />
              {claim.expense_type && (
                <span
                  className={
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium border " +
                    ({
                      personal: "bg-blue-50 text-blue-700 border-blue-200",
                      company_credit_card: "bg-violet-50 text-violet-700 border-violet-200",
                      company_debit_card: "bg-emerald-50 text-emerald-700 border-emerald-200",
                    }[claim.expense_type] || "bg-muted text-muted-foreground border-border")
                  }
                  data-testid="detail-expense-type-badge"
                >
                  <CreditCard className="h-3 w-3" />
                  {claim.expense_type_label || "—"}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 font-mono">{claim.claim_number} · {fmtDateOnly(claim.expense_date)}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" className="rounded-sm" onClick={printClaim} data-testid="print-claim-btn">
              <Printer className="h-4 w-4 mr-2" />Print Claim
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={printAllAttachments} disabled={allAttachments.length === 0} data-testid="print-attachments-btn">
              <Files className="h-4 w-4 mr-2" />Print Attachments{allAttachments.length ? ` (${allAttachments.length})` : ""}
            </Button>
            <Button variant="outline" className="rounded-sm" onClick={downloadReceiptsZip} disabled={allAttachments.length === 0} data-testid="download-receipts-btn">
              <FileArchive className="h-4 w-4 mr-2" />Download Receipts
            </Button>
            {canEditClaim && (
              <Button variant="outline" className="rounded-sm" onClick={() => navigate(`/claims/${id}/edit`)} data-testid="edit-claim-btn">
                <Pencil className="h-4 w-4 mr-2" />{canEmployeeEdit && claim.status === "rejected" ? "Edit & Resubmit" : "Edit"}
              </Button>
            )}
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Total</p>
              <p className="font-mono text-2xl font-semibold" data-testid="detail-total">{currencyFmt(claim.total_amount, claim.currency)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Meta label="Expense Type" value={claim.expense_type_label || "—"} />
        <Meta label="Employee" value={claim.employee_name} />
        <Meta label="Category" value={claim.category} />
        <Meta label="Currency" value={claim.currency} />
        <Meta label="Project" value={claim.project_code || "—"} />
        <Meta label="Approver" value={claim.approver_name || "Unassigned"} />
      </div>

      {claim.description && (
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-2">Description</h3>
          <p className="text-sm">{claim.description}</p>
        </div>
      )}

      <div className="bg-card border border-border rounded-md overflow-hidden">
        <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium p-5 pb-3">Line Items</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Receipts</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {claim.line_items.map((li, i) => (
              <TableRow key={i} data-testid={`detail-item-${i}`}>
                <TableCell className="font-medium">{li.description}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{li.category}</TableCell>
                <TableCell className="font-mono text-xs">{fmtDateOnly(li.expense_date)}</TableCell>
                <TableCell>
                  {(li.attachments && li.attachments.length) ? (
                    <div className="flex flex-wrap gap-1.5">
                      {li.attachments.map((a) => (
                        <span key={a.file_id} className="inline-flex items-center gap-0.5 bg-muted/60 rounded-sm pl-2 pr-1 py-1 text-xs max-w-[180px]" data-testid={`attachment-${a.file_id}`}>
                          <button onClick={() => viewAttachment(a)} data-testid={`view-attachment-${a.file_id}`} className="inline-flex items-center gap-1 min-w-0 hover:text-primary transition-colors" title="Open">
                            <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate">{a.filename}</span>
                          </button>
                          <button onClick={() => printAttachment(a)} data-testid={`print-attachment-${a.file_id}`} className="p-1 rounded hover:bg-background/80 transition-colors shrink-0" title="Print to A4">
                            <Printer className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right font-mono" data-testid={`detail-item-gst-${i}`}>
                  {currencyFmt(li.gst_applicable !== false ? li.amount * 3 / 23 : 0, claim.currency)}
                </TableCell>
                <TableCell className="text-right font-mono">{currencyFmt(li.amount, claim.currency)}</TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/40 font-medium">
              <TableCell colSpan={4} className="text-right text-muted-foreground uppercase text-xs tracking-wider">Totals</TableCell>
              <TableCell className="text-right font-mono" data-testid="detail-total-gst">{currencyFmt(claim.total_gst || 0, claim.currency)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{currencyFmt(claim.total_amount, claim.currency)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {claim.attachments?.length > 0 && (
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-3">Attachments</h3>
          <div className="space-y-2">
            {claim.attachments.map((a) => (
              <button key={a.file_id} onClick={() => viewAttachment(a)} data-testid={`view-attachment-${a.file_id}`}
                className="w-full flex items-center justify-between bg-muted/50 hover:bg-muted rounded-sm px-3 py-2 transition-colors text-left">
                <span className="flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-muted-foreground" />{a.filename}</span>
                <Download className="h-4 w-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}

      {(claim.status !== "pending") && (
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-2">Review Decision</h3>
          <p className="text-sm">
            {claim.status === "approved" ? "Approved" : "Rejected"} by <span className="font-medium">{claim.reviewed_by_name}</span>
          </p>
          {claim.review_comment && <p className="text-sm text-muted-foreground mt-1">"{claim.review_comment}"</p>}
        </div>
      )}

      {claim.history?.length > 0 && (
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium mb-3">History</h3>
          <div className="space-y-3">
            {claim.history.map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="capitalize font-medium">{h.action} <span className="text-muted-foreground font-normal">by {h.by}</span></p>
                  <p className="text-xs text-muted-foreground font-mono">{fmtDateTime(h.at)}</p>
                  {h.comment && <p className="text-sm text-muted-foreground mt-0.5">"{h.comment}"</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(canDecide || canForward) && (
        <div className="bg-card border border-border rounded-md p-5" data-testid="review-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm uppercase tracking-wider text-muted-foreground font-medium">Take Action</h3>
            {canForward && (
              <Dialog open={fwdOpen} onOpenChange={setFwdOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-sm" data-testid="forward-btn">
                    <Forward className="h-4 w-4 mr-2" />Forward
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Forward for approval</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">Reassign this claim to another approver to get their approval.</p>
                    <Select value={fwdTo} onValueChange={setFwdTo}>
                      <SelectTrigger data-testid="forward-select" className="rounded-sm"><SelectValue placeholder="Select approver" /></SelectTrigger>
                      <SelectContent>
                        {forwardTargets.length ? forwardTargets.map((m) => (
                          <SelectItem key={m.user_id} value={m.user_id} data-testid={`forward-option-${m.user_id}`}>{m.name} · {m.role === "admin" ? "Admin" : "Manager"}</SelectItem>
                        )) : <div className="px-2 py-1.5 text-sm text-muted-foreground">No other approvers available</div>}
                      </SelectContent>
                    </Select>
                    <Textarea placeholder="Optional note" value={fwdNote} onChange={(e) => setFwdNote(e.target.value)} className="rounded-sm" data-testid="forward-note" />
                  </div>
                  <DialogFooter>
                    <Button onClick={forward} disabled={busy || !fwdTo} className="rounded-sm" data-testid="forward-submit">
                      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Forward Claim
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {canDecide ? (
            <>
              <Textarea placeholder="Add a comment (required for rejection)" value={comment} onChange={(e) => setComment(e.target.value)} className="rounded-sm mb-4" data-testid="review-comment" />
              <div className="flex gap-3">
                <Button onClick={() => decide("approve")} disabled={busy} className="rounded-sm bg-success hover:bg-success/90 text-white" data-testid="approve-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Approve
                </Button>
                <Button onClick={() => decide("reject")} disabled={busy} variant="outline" className="rounded-sm border-danger text-danger hover:bg-danger/10" data-testid="reject-btn">
                  <XCircle className="h-4 w-4 mr-2" />Reject
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="assigned-notice">
              This claim is assigned to <span className="font-medium text-foreground">{claim.approver_name}</span>. Forward it to another manager to take action.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div className="bg-card border border-border rounded-md p-4">
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-sm font-medium mt-1">{value}</p>
  </div>
);
